import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Mail, ExternalLink } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const GIVEBUTTER_URL = "https://givebutter.com/todd-boyle-three-abide-axsa69";
const GIVEBUTTER_EMBED = "https://givebutter.com/embed/c/todd-boyle-three-abide-axsa69";

const TABS = [
  { id: "online", label: "Give Online", icon: Heart },
  { id: "check", label: "Checks", icon: Mail },
];

export const Give = () => {
  const [tab, setTab] = useState("online");

  return (
    <section
      id="give"
      data-testid="give-now-section"
      className="grain relative overflow-hidden bg-navy py-28 lg:py-36"
    >
      <div className="relative mx-auto max-w-[1500px] px-6 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <Reveal className="lg:col-span-6">
            <Eyebrow tone="gold">Partner With Us</Eyebrow>
            <h2 className="font-display mt-7 text-[2.2rem] leading-[1.08] tracking-[-0.02em] text-sand sm:text-5xl">
              Give to
              <span className="italic text-gold"> Three Abide Ministries</span>
            </h2>

            <div className="mt-8 border-l-2 border-gold/60 pl-6">
              <p className="text-sm leading-[1.8] text-sand/70">
                During this transition, contributions made directly to me will not be
                tax-deductible. I understand that this may affect your decision about continuing to
                give, and I completely respect that.
              </p>
            </div>

            <p className="mt-7 text-base leading-[1.8] text-sand/80">
              If you feel led to continue supporting the boys and me as we move forward, here are
              the current ways to give.
            </p>

            <a
              href={GIVEBUTTER_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="givebutter-external-link"
              className="btn-sheen mt-8 inline-flex items-center gap-2 rounded-full bg-crimson px-7 py-3.5 font-mono-accent text-[0.68rem] uppercase tracking-[0.18em] text-sand"
            >
              Open giving page <ExternalLink size={14} />
            </a>
          </Reveal>

          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.1}>
              <div className="glass-dark border border-sand/15 p-6 lg:p-7">
                <div className="flex flex-wrap gap-2">
                  {TABS.map((t) => {
                    const Icon = t.icon;
                    const on = tab === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        data-testid={`give-tab-${t.id}`}
                        onClick={() => setTab(t.id)}
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono-accent text-[0.68rem] uppercase tracking-[0.18em] transition-colors duration-400 ${
                          on
                            ? "bg-crimson text-sand"
                            : "border border-sand/25 text-sand/65 hover:text-sand"
                        }`}
                      >
                        <Icon size={14} /> {t.label}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-7">
                  <AnimatePresence mode="wait">
                    {tab === "online" && (
                      <motion.div
                        key="online"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.4 }}
                        data-testid="give-panel-online"
                      >
                        <p className="label-eyebrow text-sand/45">Secure online giving</p>
                        <h3 className="font-display mt-3 text-xl text-sand">
                          One-time or monthly
                        </h3>
                        <div className="mt-5 overflow-hidden border border-sand/15 bg-white">
                          <iframe
                            src={GIVEBUTTER_EMBED}
                            title="Give to Three Abide Ministries"
                            data-testid="givebutter-embed"
                            loading="lazy"
                            allow="payment *"
                            className="h-[30rem] w-full border-0"
                          />
                        </div>
                      </motion.div>
                    )}

                    {tab === "check" && (
                      <motion.div
                        key="check"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.4 }}
                        data-testid="give-panel-check"
                        className="min-h-[18rem]"
                      >
                        <p className="label-eyebrow text-sand/45">By mail</p>
                        <h3 className="font-display mt-4 text-2xl text-sand">Send a check</h3>
                        <p className="mt-3 text-sm leading-relaxed text-sand/65">
                          Make checks payable to{" "}
                          <strong className="text-sand">Mission Serve Inc.</strong> and write{" "}
                          <strong className="text-sand">Three Abide</strong> in the memo line. Mail
                          them to:
                        </p>
                        <address
                          data-testid="check-mailing-address"
                          className="font-display mt-6 border-l-2 border-gold/60 pl-5 text-lg not-italic leading-relaxed text-sand"
                        >
                          Mission Serve Inc.
                          <br />
                          5385 E Atlantic Pl
                          <br />
                          Denver, CO 80222
                        </address>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
